import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import type {DatesSetArg} from "@fullcalendar/core/index.js";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import {memo, type RefObject} from "react";
import type {BeatPlanningCalendarEvent} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import {
    CALENDAR_MAX_VISIBLE_STACKED_EVENTS,
    getCalendarEventCountSummaryCellEvents,
    getCalendarEventCountSummarySources,
} from "~/components/beatPlanningCalendar/calendarEventCountDisplay";
import type {CalendarViewMode} from "~/components/beatPlanningCalendar/calendarToolbarBus";

function toLocalDayKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatLocalTimeHm24(date: Date): string {
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function MemoizedCalendarMonthWeekDayView({
    nonYearCalendarRef,
    initialNonYearCalendarViewMode,
    currentViewType,
    isMobile,
    events,
    eventDayKeys,
    onNonYearDatesSet,
    onEventSelected,
    openDayEventsModal,
    beatEventToSelectable,
    eventOpensDaySummaryModal = false,
    useEventCountDisplay = false,
    use24HourTimeAxis = false,
}: {
    nonYearCalendarRef: RefObject<FullCalendar | null>;
    initialNonYearCalendarViewMode: CalendarViewMode;
    currentViewType: CalendarViewMode;
    isMobile: boolean;
    events: Array<
        BeatPlanningCalendarEvent & {
            backgroundColor?: string;
            borderColor?: string;
            textColor?: string;
        }
    >;
    eventDayKeys: Set<string>;
    onNonYearDatesSet: (args: DatesSetArg) => void;
    onEventSelected: (event: {
        id: string;
        title: string;
        start: Date | null;
        end: Date | null;
        allDay: boolean;
        extendedProps: unknown;
        backgroundColor?: string;
        borderColor?: string;
        textColor?: string;
    }) => void;
    openDayEventsModal: (
        date: Date,
        calendarInstanceRef: RefObject<FullCalendar | null>,
        periodEventsOverride?: Array<{
            id: string;
            title: string;
            start: Date | null;
            end: Date | null;
            allDay: boolean;
            extendedProps: unknown;
            backgroundColor?: string;
            borderColor?: string;
            textColor?: string;
        }>,
    ) => void;
    beatEventToSelectable: (event: BeatPlanningCalendarEvent) => {
        id: string;
        title: string;
        start: Date | null;
        end: Date | null;
        allDay: boolean;
        extendedProps: unknown;
        backgroundColor?: string;
        borderColor?: string;
        textColor?: string;
    };
    eventOpensDaySummaryModal?: boolean;
    useEventCountDisplay?: boolean;
    /** When true, slot labels and event time prefix use 24-hour clock (e.g. 16:00). */
    use24HourTimeAxis?: boolean;
}) {
    const collapseDayGridToCountLink = useEventCountDisplay === true;

    return (
        <div
            className={[
                "beat-planning-calendar__calendar-shell",
                currentViewType === "scheduleList" ? "beat-planning-calendar__fc-hidden" : "",
            ]
                .filter((value) => value !== "")
                .join(" ")}
            data-view={currentViewType}
        >
            <FullCalendar
                ref={nonYearCalendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView={initialNonYearCalendarViewMode}
                height={currentViewType === "dayGridMonth" ? "100%" : "auto"}
                expandRows={currentViewType === "dayGridMonth"}
                views={{
                    timeGridFourDay: {
                        type: "timeGrid",
                        duration: {days: 4},
                        buttonText: "4 Days",
                    },
                }}
                headerToolbar={false}
                allDayText="ALL DAY"
                slotLabelFormat={
                    use24HourTimeAxis === true
                        ? undefined
                        : {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                          }
                }
                slotLabelContent={
                    use24HourTimeAxis === true
                        ? (arg) => <span className="fc-slot-label-text">{formatLocalTimeHm24(arg.date)}</span>
                        : undefined
                }
                eventTimeFormat={
                    use24HourTimeAxis === true
                        ? {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              hourCycle: "h23",
                          }
                        : undefined
                }
                slotDuration="01:00:00"
                slotLabelInterval="01:00:00"
                slotEventOverlap={useEventCountDisplay === true ? false : undefined}
                scrollTime="09:00:00"
                datesSet={onNonYearDatesSet}
                dayMaxEventRows={
                    collapseDayGridToCountLink === true
                        ? CALENDAR_MAX_VISIBLE_STACKED_EVENTS
                        : isMobile === true
                          ? CALENDAR_MAX_VISIBLE_STACKED_EVENTS
                          : true
                }
                dayMaxEvents={
                    collapseDayGridToCountLink === true
                        ? CALENDAR_MAX_VISIBLE_STACKED_EVENTS
                        : isMobile === true
                          ? CALENDAR_MAX_VISIBLE_STACKED_EVENTS
                          : true
                }
                moreLinkContent={collapseDayGridToCountLink === true ? (args) => `+${args.num}` : undefined}
                // moreLinkClick={
                //     collapseDayGridToCountLink === true
                //         ? (info) => {
                //               info.jsEvent.preventDefault();
                //               openDayEventsModal(info.date, nonYearCalendarRef);
                //           }
                //         : undefined
                // }
                eventClassNames={(arg) => {
                    if (getCalendarEventCountSummarySources(arg.event.extendedProps) !== undefined) {
                        return ["beat-planning-calendar__fc-event--count-summary"];
                    }
                    return [];
                }}
                eventContent={(arg) => {
                    if (getCalendarEventCountSummarySources(arg.event.extendedProps) !== undefined) {
                        return (
                            <div
                                className="beat-planning-calendar__count-summary-badge"
                                aria-label={`${arg.event.title} more events`}
                            >
                                {arg.event.title}
                            </div>
                        );
                    }
                    return true;
                }}
                eventDidMount={(info) => {
                    if (getCalendarEventCountSummarySources(info.event.extendedProps) === undefined) {
                        return;
                    }
                    info.el.style.zIndex = "1000";
                    const harness = info.el.closest(".fc-timegrid-event-harness, .fc-daygrid-event-harness");
                    if (harness instanceof HTMLElement) {
                        harness.style.zIndex = "1000";
                    }
                }}
                events={events}
                dayHeaderContent={(arg) => {
                    if (arg.view.type === "dayGridMonth") {
                        const weekday = arg.date.toLocaleDateString(undefined, {weekday: "short"}).toUpperCase();
                        return (
                            <span className="beat-planning-calendar__month-weekday-only">{weekday}</span>
                        );
                    }
                    const weekday = arg.date.toLocaleDateString(undefined, {weekday: "short"}).toUpperCase();
                    const dayNumber = String(arg.date.getDate());
                    return (
                        <div className="beat-planning-calendar__day-header-content">
                            <span className="beat-planning-calendar__day-header-weekday">{weekday}</span>
                            <span className="beat-planning-calendar__day-header-date">{dayNumber}</span>
                        </div>
                    );
                }}
                dayCellClassNames={(arg) => {
                    const dayKey = toLocalDayKey(arg.date);
                    return eventDayKeys.has(dayKey)
                        ? "beat-planning-calendar__day-cell--has-events"
                        : "beat-planning-calendar__day-cell--empty";
                }}
                moreLinkClick={(info) => {
                    openDayEventsModal(info.date, nonYearCalendarRef);
                    // Non-string truthy return suppresses FullCalendar's default "+N more" popover.
                    return true as unknown as undefined;
                }}
                dateClick={(info) => {
                    if (currentViewType === "scheduleList") {
                        return;
                    }
                    openDayEventsModal(info.date, nonYearCalendarRef);
                }}
                eventClick={(info) => {
                    const summarySources = getCalendarEventCountSummarySources(info.event.extendedProps);
                    if (summarySources !== undefined && info.event.start !== null) {
                        const cellEvents = getCalendarEventCountSummaryCellEvents(info.event.extendedProps);
                        openDayEventsModal(
                            info.event.start,
                            nonYearCalendarRef,
                            cellEvents?.map((event) => beatEventToSelectable(event)),
                        );
                        return;
                    }
                    if (eventOpensDaySummaryModal === true && info.event.start !== null) {
                        openDayEventsModal(info.event.start, nonYearCalendarRef);
                        return;
                    }
                    onEventSelected({
                        id: info.event.id,
                        title: info.event.title,
                        start: info.event.start,
                        end: info.event.end,
                        allDay: info.event.allDay,
                        extendedProps: info.event.extendedProps,
                        backgroundColor: info.event.backgroundColor,
                        borderColor: info.event.borderColor,
                        textColor: info.event.textColor,
                    });
                }}
            />
        </div>
    );
}

export const CalendarMonthWeekDayView = memo(MemoizedCalendarMonthWeekDayView);
